import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  PageHeader,
  PAGE_HEADER_MODE_STORAGE_KEY,
  type PageHeaderProps,
} from "@noiman/ui/page-header";
import { captureAnalyticsEvent } from "@/hooks/useAnalytics";
import { useTeam } from "@/contexts/team.context";
import { useGlobalSearch } from "@/contexts/global-search.context";
import { useLocation } from "react-router";
import { useAuth, useAuthPending } from "@/contexts/auth.context";
import { useMemberShell } from "@/contexts/member-shell.context";
import { useLuagateTheme } from "@/contexts/luagate-theme.context";
import lgLogoLightUrl from "@/components/luagate-ui/assets/yumesaku-engineer-logo.svg?url";
import lgLogoDarkUrl from "@/components/luagate-ui/assets/yumesaku-engineer-logo-dark.svg?url";
import { HEADER_NAV_ART } from "./header-nav-art";

/* 札の CSS と取得ごと、窓を初めて開くまで読まない。IT資格・面接対策・転職の窓は静的な一覧なので shared-ui の PageHeader が描く */
const CoursesMenuPanel = lazy(() => import("./CoursesMenuPanel"));
/* ログイン後の鈴とアバター (/luagate の帯と同じ部品)。lg.css ごと、ログインした人の画面でだけ読む */
const HeaderNewsBell = lazy(() => import("./HeaderMemberControls").then((m) => ({ default: m.HeaderNewsBell })));
const HeaderAvatar = lazy(() => import("./HeaderMemberControls").then((m) => ({ default: m.HeaderAvatar })));

/* 部品を読むまでの場所取り。鈴の 44px 角と、アバターの丸の 40px */
const BELL_FALLBACK = <span aria-hidden="true" className="inline-block h-11 w-11 shrink-0" />;
const AVATAR_FALLBACK = <span aria-hidden="true" className="inline-block h-10 w-10 shrink-0 rounded-full bg-second" />;

/* コースの窓は 4 列で 2 段 (2026-10-26 に 3 列から増やした)。札 1 枚が約 220px で、題が 3 行に収まる幅 */
const COURSES_MENU_PANEL_WIDTH_CLASS_NAME = "w-[min(944px,calc(100vw-48px))]";
const COURSES_MENU_FALLBACK = (
  <div className="h-[420px]" aria-hidden="true" />
);

/*
本人を読み終えるまで帯の右端を空けておく上限。/me が返らないまま止まっても、ログインの入口が永久に消えないようにする
*/
const AUTH_PENDING_MAX_MS = 6000;

function usePendingUpTo(pending: boolean, maxMs: number): boolean {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => setExpired(true), maxMs);
    return () => window.clearTimeout(timer);
  }, [pending, maxMs]);
  return pending && !expired;
}

type AppPageHeaderProps = Omit<PageHeaderProps, "teams">;

const slideQaLocalOnly = import.meta.env.VITE_SLIDE_QA_LOCAL_ONLY === "1";

/*
本文が暗い版を持つ公開の頁。ここだけ帯も暗い版に従わせる。
見分け方は頁の住所の一覧。本文が暗い版を持つ頁を足すときは、ここに住所を足す。
一覧に無い古い公開の頁 (用語辞典、ガイド、ツールなど) は本文が明るいままなので、帯も明るいままにする。
帯だけ暗く本文が白い姿を出さないため (2026-10-20 の点検 #3)
*/
export const DARK_BODY_PATHS: ReadonlySet<string> = new Set(["/", "/schools"]);

export function AppPageHeader(props: AppPageHeaderProps) {
  const { teams, planInfo, luagateEntry } = useTeam();
  const { openSearch } = useGlobalSearch();
  const location = useLocation();
  const authLoading = usePendingUpTo(useAuthPending(), AUTH_PENDING_MAX_MS);
  const { user: authUser } = useAuth();
  /*
  ログインしているかは本人 (/me) から決める。呼び出し側が userEmail を渡す頁だけで決めていたので、
  渡さない頁 (/about、/privacy-policy ほか) では、チームの一覧 (useTeam) は届くのにアバターが出ず、
  「所属チーム」と「ログイン / 新規登録」が同時に並んでいた (2026-10-25)
  */
  const userEmail = props.userEmail ?? authUser?.email;
  const userName = props.userName ?? authUser?.name;
  const isLoggedIn = Boolean(userName || userEmail);
  const followsSavedMode = DARK_BODY_PATHS.has(location.pathname);
  const savedMode = useSavedHeaderMode(isLoggedIn && followsSavedMode);
  // /luagate の外枠の中では外枠が帯を持つ。受講生が開く公開の頁 (tone) は、帯を /luagate の明暗に従わせる
  const shell = useMemberShell();
  // レベルは /luagate の帯と同じく本人 (/me) の levelInfo から。呼び出し側が userLevel だけを渡す頁でも同じ値になる
  const { theme: luagateTheme } = useLuagateTheme();

  // 支払い管理は自前の画面 (/settings/subscription) に置いた。
  // 以前は Stripe のホスト型ポータルへ fetch してから飛ばしていたので、
  // 押してから遷移するまで無反応な時間があり、契約の無い人は
  // 何の説明も無く /code/pricing に投げ返されていた。ただの遷移で足りる
  const handlePaymentClick = () => {
    window.location.href = "/settings/subscription";
  };

  const headerMode = props.mode ?? (shell === "tone" ? luagateTheme : savedMode);
  const coursesMenu = useMemo<PageHeaderProps["coursesMenu"]>(
    () => ({
      panelWidthClassName: COURSES_MENU_PANEL_WIDTH_CLASS_NAME,
      renderPanel: ({ close }) => (
        <Suspense fallback={COURSES_MENU_FALLBACK}>
          <CoursesMenuPanel onNavigate={close} mode={headerMode === "dark" ? "dark" : undefined} />
        </Suspense>
      ),
    }),
    [headerMode],
  );

  if (shell === "frame") return null;

  const barMode = headerMode === "dark" ? "dark" : "light";
  const level =
    authUser?.levelInfo ??
    (props.userLevel !== undefined
      ? { level: props.userLevel, rank: props.userRank ?? "", totalXp: props.userTotalXp ?? 0, xpToNextLevel: props.userXpToNextLevel ?? 0, progressPercent: 0 }
      : null);

  return (
    <PageHeader
      {...props}
      userEmail={userEmail}
      userName={userName}
      // 本人を読み終えるまで「ログイン / 新規登録」を出さない (ログイン済みの人に一瞬出ていた)
      authPending={props.authPending ?? authLoading}
      // アバターの札と同じレベルを渡す (釦の読み上げの名前に入れる)
      userLevel={level?.level ?? props.userLevel}
      coursesMenu={props.coursesMenu ?? coursesMenu}
      // IT資格・面接対策・転職の窓と引き出しの絵 (中身の一覧は headerNav.ts、絵は今ある帯と札の絵)
      navArt={props.navArt ?? HEADER_NAV_ART}
      /*
      ヘッダのメニューのクリックを記録する。**これまで1件も測っていなかった。**
      メニューを 12 項目から 3 項目に減らしたので、減らしたことが効いたのか
      単に導線が消えただけなのかを、あとから数字で見分けられるようにしておく。

      計測は shared-ui に持たせない。あの package は posthog に依存して
      いないので、送り先を決めるのはこちら側の仕事
      */
      onNavClick={
        props.onNavClick ??
        ((detail) =>
          captureAnalyticsEvent("header_nav_clicked", {
            nav_label: detail.label,
            nav_to: detail.to,
            nav_tab: detail.tab,
            from_path: location.pathname,
          }))
      }
      showCampaignBanner={slideQaLocalOnly ? false : props.showCampaignBanner}
      currentPath={props.currentPath ?? location.pathname}
      teams={teams.map((t) => ({
        name: t.name,
        slug: t.slug,
        iconUrl: t.iconUrl ?? undefined,
      }))}
      notifications={
        props.notifications ??
        (isLoggedIn ? (
          <Suspense fallback={BELL_FALLBACK}>
            <HeaderNewsBell mode={barMode} />
          </Suspense>
        ) : undefined)
      }
      avatar={
        props.avatar ??
        (isLoggedIn ? (
          <Suspense fallback={AVATAR_FALLBACK}>
            <HeaderAvatar mode={barMode} name={userName} level={level} />
          </Suspense>
        ) : undefined)
      }
      dashboardHref={props.dashboardHref ?? (luagateEntry ? "/luagate" : undefined)}
      currentPlan={props.currentPlan ?? planInfo?.planName}
      onPaymentClick={props.onPaymentClick ?? handlePaymentClick}
      onSearchClick={props.onSearchClick ?? openSearch}
      mode={headerMode}
      // 受講生には /luagate と同じ印を出し、研究所と女性向けの段を外す
      hideTopStrip={props.hideTopStrip ?? shell === "tone"}
      brand={props.brand ?? (shell === "tone" ? { href: "/luagate", logo: <img src={headerMode === "dark" ? lgLogoDarkUrl : lgLogoLightUrl} alt="ゆめさくエンジニア" width={156} height={28} className="block h-7 w-auto" /> } : undefined)}
      // 明暗を外から渡したときは端末の覚えを読まない
      restoreSavedMode={props.restoreSavedMode ?? (isLoggedIn && followsSavedMode && props.mode === undefined && shell === "none")}
    />
  );
}

/*
ログイン後に /luagate で暗い版を選んだ人は、公開の頁の帯も暗くする。明暗は端末の
luagate-theme (LuagateThemeProvider と同じ鍵) が持つ。ログインしていない人は、同じ端末で
前に誰かが選んだ暗い版を引き継がない
*/
export function useSavedHeaderMode(isLoggedIn: boolean): "light" | "dark" {
  const [mode, setMode] = useState<"light" | "dark">("light");
  useEffect(() => {
    if (!isLoggedIn) {
      setMode("light");
      return;
    }
    try {
      setMode(localStorage.getItem(PAGE_HEADER_MODE_STORAGE_KEY) === "dark" ? "dark" : "light");
    } catch {
      setMode("light");
    }
  }, [isLoggedIn]);
  return mode;
}
